import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
  description: string;
  action: () => void;
  group: 'Navigation' | 'Search' | 'Help';
}

interface UseKeyboardShortcutsOptions {
  enabled?: boolean;
  preventDefault?: boolean;
}

/**
 * Basic keyboard shortcut hook
 */
export function useKeyboardShortcuts(
  shortcuts: KeyboardShortcut[],
  options: UseKeyboardShortcutsOptions = {}
) {
  const { enabled = true, preventDefault = true } = options;
  const shortcutsRef = useRef(shortcuts);

  // Update shortcuts ref when they change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        // Allow Ctrl+K even in inputs (for search)
        if (!(event.ctrlKey && event.key.toLowerCase() === 'k')) {
          return;
        }
      }

      // Find matching shortcut
      const matchingShortcut = shortcutsRef.current.find((shortcut) => {
        const keyMatches = shortcut.key.toLowerCase() === event.key.toLowerCase();
        const ctrlMatches = shortcut.ctrlKey ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey;
        const shiftMatches = shortcut.shiftKey ? event.shiftKey : !event.shiftKey;
        const altMatches = shortcut.altKey ? event.altKey : !event.altKey;
        const metaMatches = shortcut.metaKey ? event.metaKey : !event.metaKey;

        return keyMatches && ctrlMatches && shiftMatches && altMatches && metaMatches;
      });

      if (matchingShortcut) {
        if (preventDefault) {
          event.preventDefault();
        }
        matchingShortcut.action();
      }
    },
    [enabled, preventDefault]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}

/**
 * Hook for global application keyboard shortcuts
 * Includes navigation, search, and common actions
 */
export function useGlobalKeyboardShortcuts(onToggleHelp: () => void) {
  const navigate = useNavigate();
  const lastGRef = useRef<number>(0);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger in inputs except for specific shortcuts
      const target = event.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      // Help shortcut (?)
      if (event.key === '?' && !isInput) {
        event.preventDefault();
        onToggleHelp();
        return;
      }

      // Ctrl+K for search
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        const searchInput = document.querySelector('input[type="text"][placeholder*="Search"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
        return;
      }

      // G + key navigation
      if (isInput) return;

      if (event.key.toLowerCase() === 'g' && !event.ctrlKey && !event.metaKey) {
        const now = Date.now();
        lastGRef.current = now;
        return;
      }

      // Check if we just pressed G (within 1 second)
      const now = Date.now();
      if (now - lastGRef.current < 1000 && lastGRef.current > 0) {
        const key = event.key.toLowerCase();
        event.preventDefault();
        lastGRef.current = 0;

        switch (key) {
          case 'd':
            navigate('/');
            break;
          case 'f':
            navigate('/flows');
            break;
          case 'm':
            navigate('/modems');
            break;
          case 't':
            navigate('/tests');
            break;
          case 'a':
            navigate('/analytics');
            break;
          case 'o':
            navigate('/organization');
            break;
          case 's':
            navigate('/settings');
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, onToggleHelp]);

  const shortcuts: KeyboardShortcut[] = [
    // Search
    {
      key: 'k',
      ctrlKey: true,
      description: 'Open search',
      action: () => {},
      group: 'Search',
    },

    // Navigation (G + key combos)
    {
      key: 'd',
      description: 'Go to Dashboard (G then D)',
      action: () => navigate('/'),
      group: 'Navigation',
    },
    {
      key: 'f',
      description: 'Go to Flows (G then F)',
      action: () => navigate('/flows'),
      group: 'Navigation',
    },
    {
      key: 'm',
      description: 'Go to Modems (G then M)',
      action: () => navigate('/modems'),
      group: 'Navigation',
    },
    {
      key: 't',
      description: 'Go to Tests (G then T)',
      action: () => navigate('/tests'),
      group: 'Navigation',
    },
    {
      key: 'a',
      description: 'Go to Analytics (G then A)',
      action: () => navigate('/analytics'),
      group: 'Navigation',
    },
    {
      key: 'o',
      description: 'Go to Organization (G then O)',
      action: () => navigate('/organization'),
      group: 'Navigation',
    },
    {
      key: 's',
      description: 'Go to Settings (G then S)',
      action: () => navigate('/settings'),
      group: 'Navigation',
    },

    // Help
    {
      key: '?',
      description: 'Show keyboard shortcuts',
      action: onToggleHelp,
      group: 'Help',
    },
  ];

  return { shortcuts };
}

/**
 * Format keyboard shortcut for display
 */
export function formatKeyboardShortcut(shortcut: KeyboardShortcut): string {
  const parts: string[] = [];

  if (shortcut.ctrlKey || shortcut.metaKey) {
    parts.push('⌘'); // or 'Ctrl' depending on platform
  }
  if (shortcut.altKey) {
    parts.push('⌥'); // or 'Alt'
  }
  if (shortcut.shiftKey) {
    parts.push('⇧'); // or 'Shift'
  }

  // Special cases for navigation
  if (shortcut.group === 'Navigation') {
    parts.push('G');
  }

  parts.push(shortcut.key.toUpperCase());

  return parts.join(' + ');
}
